export interface RingGeometry {
    /** Full circumference of the ring at the given radius. */
    circumference: number;
    /** `stroke-dashoffset` for the accent arc: 0 = full ring, circumference = empty. */
    dashOffset: number;
    /** Overall SVG viewport side length: `radius * 2 + strokeWidth * 2`. */
    size: number;
    /** Centre coordinate for both the cx/cy of the circle and the viewport. */
    center: number;
}

/**
 * Pure geometry for the countdown ring. No React, no DOM, no timers — same
 * input always produces the same output, so DS-15/DS-17 can drive it purely
 * off a `value` prop supplied by the caller (see `useNow`).
 */
export function ringGeometry(value: number, radius: number, strokeWidth: number): RingGeometry {
    // Callers derive `value` as `1 - remaining / total`. A rushed or
    // zero-duration build makes that 0/0, and Math.min/max propagate the
    // resulting NaN straight into stroke-dashoffset, where the ring renders
    // wrong rather than empty. Treat anything non-finite as no progress.
    const clamped = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - clamped);
    const size = radius * 2 + strokeWidth * 2;
    const center = size / 2;

    return { circumference, dashOffset, size, center };
}
